"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Download } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors py-2 border-b border-border/50" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState<"EN" | "ES">("EN");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/cohcc_foundation_logo.png" alt="COHCC Foundation Logo" className="h-[52px] w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center px-4">
          <MenuProvider menu_id="39" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center gap-6">
          {}
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <span className={cn(lang === "EN" ? "text-primary font-bold" : "")}>EN</span>
            <Switch checked={lang === "ES"} onCheckedChange={checked => setLang(checked ? "ES" : "EN")} className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-primary/50" />
            <span className={cn(lang === "ES" ? "text-primary font-bold" : "")}>ES</span>
          </div>

          {}
          <Button variant="secondary" className="font-bold shadow-md hover:shadow-lg transition-all">
            Donate
          </Button>
        </div>

        {}
        <div className="flex md:hidden items-center gap-4">
           <Button variant="secondary" size="sm" className="font-bold text-xs">
            Donate
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-20 left-0 w-full bg-background border-b border-border shadow-lg animate-in slide-in-from-top-5 fade-in duration-200">
          <div className="flex flex-col p-4 space-y-6">
            <MenuProvider menu_id="39">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="px-6 pb-6 border-t border-border pt-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Language</span>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className={cn(lang === "EN" ? "text-primary font-bold" : "text-muted-foreground")}>EN</span>
                  <Switch checked={lang === "ES"} onCheckedChange={checked => setLang(checked ? "ES" : "EN")} />
                  <span className={cn(lang === "ES" ? "text-primary font-bold" : "text-muted-foreground")}>ES</span>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}